<script setup>
/**
 * One connection, written or rewritten. THE PLAN, §4 P8.
 *
 * ONE COMPONENT SERVES BOTH `waslo_connection_new` AND `waslo_connection_edit`, and the
 * transition between them is handled exactly as `TaskEditorPage` handles it: the create
 * adopts the response, sets the id before the navigation so the page is an edit in one
 * tick, then `router.replace`s onto the edit route — replace, not push, because "new
 * connection" is not somewhere to come back to with the Back button.
 *
 * THREE THINGS THIS EDITOR HAS THAT THE TASK EDITOR DOES NOT:
 *
 *  1. **A FIELD THAT ONLY EXISTS ONCE.** The short name is a box on a create and a sentence
 *     on an edit. A task attaches to a connection BY NAME and there is no rename upstream
 *     that would rewrite those references, so a rename would silently detach every task
 *     using it — the BFF's PATCH does not accept `name` at all, and this page never sends
 *     one (`routes/api/tools.ts` decision 1).
 *
 *  2. **SERVER FIELD MESSAGES ARE PINNED, NOT BANNERED.** Everything this form can check it
 *     checks before the write, but the rules that are genuinely the sidecar's — a name
 *     already taken, a host it will not accept — can only be known by asking. The BFF
 *     answers those as `validation_failed` with `fields` keyed by the same names this form
 *     uses, so they land under the control that caused them. A 422 with no `fields` is
 *     `upstream_rejected` by contract and goes to the bar, which is where a sentence with
 *     nothing naming a control belongs.
 *
 *  3. **A BUTTON THAT CALLS A REAL SYSTEM.** The dry run runs against what is SAVED, so it
 *     is offered only on a saved connection and only while the page is clean — see
 *     `ConnectionTestPanel.vue` for why testing something other than what is on screen is
 *     the worst possible outcome for that panel.
 *
 * ── TWO THINGS ADDED WITH THE COMPOSER, AND WHY THEY BELONG HERE ──────────────────────
 *
 *  4. **THE WAY BACK (`?return=` + `?at=`).** A step of a task that needs a system it does
 *     not have offers to make one. Without this she lands at the FRONT of a rail she was
 *     eight screens into, which is the same as losing the work. `return` is a route NAME
 *     and it is checked against `router.hasRoute()` and against our own prefix before it
 *     is used — an unvalidated return target is an open redirect in miniature, and this
 *     one would carry a freshly minted credential page's navigation with it. Everything
 *     that does not resolve is simply dropped and the page behaves exactly as it did.
 *
 *  5. **AN UNSAVED-WORK GUARD.** This page carries about a hundred controls and had none:
 *     one click on a sidebar row took the lot. Only Personality and the task editor had
 *     one. The mechanism is Personality's, including the signature rule that file paid
 *     for in production; what is NOT Personality's is the dialog — the confirmation opens
 *     in the docked bar instead, because every teleported overlay in this codebase reaches
 *     the Vuex store during render and throws after a unit test has resolved, which can
 *     only ever fail an exit code.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import ConnectionForm from '../../components/ConnectionForm.vue';
import ConnectionTestPanel from '../../components/ConnectionTestPanel.vue';
import RestoreHistory from '../../components/RestoreHistory.vue';
import WasloPageLayout from '../../components/WasloPageLayout.vue';
import WasloSaveBar from '../../components/WasloSaveBar.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloApi } from '../../composables/useWasloApi.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { toIntlLocale } from '../../helpers/intl.js';
import {
  blankDraft,
  changesWhatIsSent,
  connectionPatch,
  draftToCreate,
  draftToWire,
  purposeOf,
  toDraft,
  validateDraft,
} from '../../helpers/connection-draft.js';
import { WASLO_ERROR_CODES, wasloApi } from '../../api/client.js';

const { t, locale } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();

const positiveInt = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const connectionId = ref(positiveInt(route?.params?.toolId));
const isNew = computed(() => connectionId.value === null);

const draft = ref(blankDraft());
const savedWire = ref(draftToWire(blankDraft()));
const savedRow = ref(null);

/** Only the keys that changed. `save` sends exactly this, and nothing else. */
const patch = computed(() => connectionPatch(draft.value, savedWire.value));
const isDirty = computed(() => Object.keys(patch.value).length > 0);

/**
 * True when the row is saved OFF and this draft changes what it sends. The sidecar refuses a
 * switch-on in that same save (`http/tools.ts` `requestChanged`), because the test that
 * worked was a test of what was sent before. So the switch locks, and a draft that was
 * switched on BEFORE the edit is refused here rather than sent to be refused.
 */
const sendsSomethingNew = computed(
  () =>
    !isNew.value &&
    savedRow.value?.enabled === false &&
    changesWhatIsSent(patch.value)
);
/**
 * P-3.4: switching ON needs a test that worked. `lastTestOkAt` ABSENT means a BFF older than
 * this screen, which is "unknown", never "untested": the switch stays usable. A row that is
 * already ON stays switchable too: the BFF refuses only an OFF-to-ON change of an untested
 * row, and turning a live row off and back on in the draft sends nothing at all. A row that
 * is OFF and tested loses the switch while the draft changes what it sends, as upstream.
 */
const canSwitchOn = computed(
  () =>
    !isNew.value &&
    (savedRow.value?.enabled === true ||
      savedRow.value?.lastTestOkAt === undefined ||
      (savedRow.value?.lastTestOkAt !== null && !sendsSomethingNew.value))
);
const testState = computed(() => {
  if (isNew.value) return 'untested';
  // Only a test that WORKED can be un-proved; an untested row already says "try it first".
  if (
    sendsSomethingNew.value &&
    typeof savedRow.value?.lastTestOkAt === 'string'
  ) {
    return 'changed';
  }
  if (savedRow.value?.lastTestOkAt !== null) return '';
  return savedRow.value?.enabled ? 'not_tried' : 'untested';
});

const fieldErrors = ref({});
const formError = ref('');
const isSaving = ref(false);
const saveFailed = ref(false);
const savedAt = ref(null);
const savedSections = ref('');
const isRemoving = ref(false);
const confirmingRemoval = ref(false);
/**
 * Set by this page immediately before a navigation IT made — after a create, after a
 * removal — so the unsaved-work guard below does not ask whether to abandon work that
 * has just been saved, or a connection that no longer exists. Declared here rather than
 * beside the guard because `save()` is written before it and reads it.
 */
const bypassGuard = ref(false);

// `immediate: false` — a new connection has nothing to fetch, and a composable that starts
// LOADING on mount would spin forever over a form that is already complete.
const connection = useWasloApi(
  signal => wasloApi.getConnection(accountId.value, connectionId.value, signal),
  { immediate: false }
);

// ── the dry run's answer ─────────────────────────────────────────────────────────────
// Declared here, beside `adopt`, because a saved row is what invalidates it.
const isTrying = ref(false);
const tryResult = ref(null);
const tryError = ref('');
const tryFields = ref({});
/**
 * Which saved row an answer belongs to. `adopt` moves it on, and a dry run that was asked
 * of an older row drops its answer instead of showing it over the new one.
 */
const savedGeneration = ref(0);

/**
 * A NEW SAVED ROW MAKES ANY EARLIER TEST ANSWER A DESCRIPTION OF A CALL THAT NO LONGER
 * EXISTS (P-3.4). A preview of a change still on screen after she saves a new address
 * would let "Send it for real" reach that address although she was only ever shown the
 * old one, and the sidecar would stamp the test that unlocks the switch. So a save clears
 * the answer, and a dry run still in flight when it lands is dropped on arrival.
 */
const forgetTryAnswer = () => {
  savedGeneration.value += 1;
  tryResult.value = null;
  tryError.value = '';
  tryFields.value = {};
};

const adopt = value => {
  savedRow.value = value;
  draft.value = toDraft(value);
  savedWire.value = draftToWire(draft.value);
  fieldErrors.value = {};
  formError.value = '';
  forgetTryAnswer();
};

/**
 * Whether there is a form to show yet — OURS, not the read composable's, for the reason
 * `TaskEditorPage` writes out in full: after a create there IS an id but that composable
 * has still never run, so reading its flag puts a spinner over a form the owner just
 * successfully saved and takes the save bar off screen with it.
 */
const ready = ref(false);

const historyRef = ref(null);
const loadConnection = async () => {
  ready.value = false;
  await connection.reload();
  if (connection.data.value) adopt(connection.data.value);
  ready.value = true;
};

onMounted(() => {
  if (isNew.value) {
    ready.value = true;
    return;
  }
  loadConnection();
});

const isFetching = computed(() => !ready.value);

const errorMessage = computed(() => {
  const error = connection.error.value;
  if (!error) return '';
  return error.code === WASLO_ERROR_CODES.NOT_FOUND
    ? t('WASLO.SETTINGS.CONNECTIONS.EDITOR.NOT_FOUND')
    : t('WASLO.COMMON.ERROR_BODY');
});

const headerTitle = computed(() => {
  if (isNew.value) return t('WASLO.SETTINGS.CONNECTIONS.EDITOR.NEW_TITLE');
  return savedRow.value?.name || t('WASLO.SETTINGS.CONNECTIONS.TITLE');
});

const listUrl = computed(() =>
  accountId.value === null
    ? null
    : frontendURL(`accounts/${accountId.value}/waslo/settings/connections`)
);

// ── the way back ───────────────────────────────────────────────────────────────────
// A step id, in exactly the spelling `schema.ts` allows one. Anything else is not a step
// she came from; it is somebody's idea of what this query string might also carry.
const STEP_ID = /^[A-Za-z0-9_-]{1,32}$/;
/** Our own routes only. `hasRoute` proves a name is registered, not that it is ours. */
const OURS = /^waslo_[a-z0-9_]+$/;

// A repeated query key arrives as an ARRAY, and "the first one wins" is how a smuggled
// second value gets read by one side and not the other. There is no first one: it is not
// a string, so there is no value.
const queryString = value => (typeof value === 'string' ? value : '');

const returnName = computed(() => {
  const wanted = queryString(route?.query?.return);
  if (!wanted || !OURS.test(wanted)) return null;
  // The whole check, in one call: a name the router does not carry would throw inside
  // `router.resolve` — the same throw that blanks the entire primary nav from inside
  // `SidebarGroup`'s computed — and a name it does carry but we did not write is not
  // somewhere this page gets to send anybody.
  return typeof router?.hasRoute === 'function' && router.hasRoute(wanted)
    ? wanted
    : null;
});

const returnStep = computed(() => {
  const at = queryString(route?.query?.at);
  return STEP_ID.test(at) ? at : '';
});

const returnId = computed(() => {
  const id = queryString(route?.query?.returnId);
  return /^[0-9]{1,12}$/.test(id) ? id : '';
});

/** The `:param` names a registered route's own path declares. */
const paramNamesOf = name => {
  const record = (router?.getRoutes?.() ?? []).find(one => one.name === name);
  if (!record || typeof record.path !== 'string') return [];
  return [...record.path.matchAll(/:([A-Za-z0-9_]+)/g)].map(match => match[1]);
};

/**
 * Where "back" goes, or `null` for the ordinary case — and `null` is also the answer for
 * a return target this page cannot BUILD. A route that declares an id and was handed none
 * would resolve to a broken URL, so it is refused exactly like an unregistered name.
 */
const returnTo = computed(() => {
  const name = returnName.value;
  if (!name || accountId.value === null) return null;
  const params = { accountId: String(accountId.value) };
  const extra = paramNamesOf(name).find(one => one !== 'accountId');
  if (extra) {
    if (!returnId.value) return null;
    params[extra] = returnId.value;
  }
  const query = {};
  if (returnStep.value) {
    query.step = 'steps';
    query.at = returnStep.value;
  }
  return { name, params, query };
});

/**
 * The same place, plus the connection that was just made — so the step she left picks it
 * up already selected rather than asking her to find it in a list she has now grown.
 * A connection is attached BY NAME (`routes/api/tools.ts` decision 1: there is no rename),
 * so the name is what travels.
 */
const returnWithConnection = name => {
  const to = returnTo.value;
  if (!to) return null;
  return { ...to, query: { ...to.query, connection: name } };
};

const backTo = computed(() => returnTo.value ?? listUrl.value ?? '');

const backLabel = computed(() =>
  returnTo.value
    ? t('WASLO.SETTINGS.CONNECTIONS.EDITOR.RETURN')
    : t('WASLO.SETTINGS.CONNECTIONS.TITLE')
);

const signInSaved = computed(() => savedRow.value?.signInSaved === true);

// Read off what is SAVED, like `params` beside it: the panel calls the stored row. The server's
// own list (P-3.1), so the boxes are exactly the details it refuses to send blank (P-3.7).
const savedContactDetails = computed(
  () => savedRow.value?.contactDetails ?? []
);

/** P-3.8: the saved connection names a sign-in and nothing is stored behind it. */
const savedNeedsSignIn = computed(
  () =>
    (savedRow.value?.auth?.type ?? 'none') !== 'none' &&
    savedRow.value?.signInSaved === false
);

// ── the commit point ───────────────────────────────────────────────────────────────
// `patch` and `isDirty` are declared with the draft above: the switch's gate reads them.

const SECTION_LABEL_KEY = {
  name: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.ABOUT.HEADING',
  description: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.ABOUT.HEADING',
  method: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.CALL.HEADING',
  url: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.CALL.HEADING',
  params: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.DETAILS.HEADING',
  body: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.BODY.HEADING',
  auth: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.SIGN_IN.HEADING',
  secret: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.SIGN_IN.HEADING',
  keepFields: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.KEEP.HEADING',
  enabled: 'WASLO.SETTINGS.CONNECTIONS.EDITOR.STATE.HEADING',
};

const nameSections = keys => {
  const names = [];
  keys.forEach(key => {
    const label = SECTION_LABEL_KEY[key];
    // Two keys can share one section; naming it twice reads like a bug.
    if (label && !names.includes(label)) names.push(label);
  });
  return names.map(label => t(label)).join(t('WASLO.COMMON.LIST_SEPARATOR'));
};

const pendingSections = computed(() => nameSections(Object.keys(patch.value)));

const savedTime = computed(() =>
  savedAt.value
    ? new Intl.DateTimeFormat(toIntlLocale(locale.value), {
        hour: 'numeric',
        minute: '2-digit',
      }).format(savedAt.value)
    : ''
);

const statusKey = name => `WASLO.SETTINGS.CONNECTIONS.EDITOR.STATUS.${name}`;

// Ordered by urgency, not by history: in flight beats a stale failure, a failure beats
// "you have edits", and a new connection always has something to commit.
const saveState = computed(() => {
  if (isSaving.value) return 'saving';
  if (saveFailed.value) return 'error';
  if (isNew.value || isDirty.value) return 'dirty';
  if (savedAt.value) return 'saved';
  return 'idle';
});

const saveStatus = computed(() => {
  if (isSaving.value) return t(statusKey('SAVING'));
  if (saveFailed.value) return t(statusKey('ERROR'));
  if (isNew.value) return t(statusKey('NEW'));
  if (isDirty.value) return t(statusKey('DIRTY'));
  if (savedAt.value) return t(statusKey('SAVED'), { time: savedTime.value });
  return t(statusKey('IDLE'));
});

const saveDetail = computed(() => {
  if (isSaving.value) return '';
  // A sentence the server sent that names no control belongs here, beside the button,
  // not under a box it is not about.
  if (saveFailed.value) return formError.value || t(statusKey('ERROR_DETAIL'));
  if (isNew.value) return t(statusKey('NEW_DETAIL'));
  if (isDirty.value) {
    return t(statusKey('DIRTY_DETAIL'), { sections: pendingSections.value });
  }
  if (savedAt.value) {
    return t(statusKey('SAVED_DETAIL'), { sections: savedSections.value });
  }
  return t(statusKey('IDLE_DETAIL'));
});

const saveLabel = computed(() =>
  isNew.value
    ? t('WASLO.SETTINGS.CONNECTIONS.EDITOR.CREATE')
    : t('WASLO.SETTINGS.CONNECTIONS.EDITOR.SAVE')
);

const onDraftChange = value => {
  // A message about the switch is about THAT position of it. Moving the switch is the answer
  // to it, so the message goes; every other section keeps its own until the next save.
  if (value.enabled !== draft.value.enabled && fieldErrors.value.enabled) {
    fieldErrors.value = Object.fromEntries(
      Object.entries(fieldErrors.value).filter(([name]) => name !== 'enabled')
    );
  }
  draft.value = value;
  // A failure is about the attempt, not about the page: the moment she changes anything
  // the message stops being true, so it stops being shown.
  if (saveFailed.value) {
    saveFailed.value = false;
    formError.value = '';
  }
};

/** Server-side field messages, pinned to the same keys the form already uses. */
const applyServerFields = error => {
  if (error?.fields) {
    fieldErrors.value = { ...fieldErrors.value, ...error.fields };
    return true;
  }
  return false;
};

const save = async () => {
  if (isSaving.value) return;
  if (!isNew.value && !isDirty.value) return;

  const errors = validateDraft(draft.value, t, {
    isNew: isNew.value,
    signInSaved: signInSaved.value,
    savedUrl: savedRow.value?.url ?? '',
  });
  // The sidecar's enable gate, asked before the write: a switch-on in the same save as an
  // edit to what is sent is refused upstream every time, so it is not sent at all.
  if (draft.value.enabled && sendsSomethingNew.value && !canSwitchOn.value) {
    errors.enabled = t('WASLO.SETTINGS.CONNECTIONS.EDITOR.STATE.CHANGED');
  }
  fieldErrors.value = errors;
  if (Object.keys(errors).length > 0) return;

  isSaving.value = true;
  saveFailed.value = false;
  formError.value = '';
  // Read the keys BEFORE the write: `adopt` clears the diff, so afterwards there is
  // nothing left to name in "what was saved".
  const carried = Object.keys(patch.value);
  try {
    if (isNew.value) {
      const created = await wasloApi.createConnection(
        accountId.value,
        draftToCreate(draft.value)
      );
      adopt(created);
      connectionId.value = created.id;
      savedSections.value = '';
      savedAt.value = new Date();
      useAlert(t('WASLO.SETTINGS.CONNECTIONS.EDITOR.CREATED'));
      if (accountId.value !== null) {
        // A connection made FOR a step goes straight back to that step, carrying its own
        // name so the step can select it. Otherwise the create becomes an edit in place,
        // exactly as before — `replace`, not `push`, because "new connection" is not
        // somewhere to come back to with the Back button.
        const back = returnWithConnection(created.name);
        // `adopt` has already cleared the diff, so the guard would pass anyway — armed
        // explicitly because "it happens to be clean here" is not a thing to rely on.
        bypassGuard.value = true;
        await router.replace(
          back ??
            frontendURL(
              `accounts/${accountId.value}/waslo/settings/connections/${created.id}`
            )
        );
      }
    } else {
      // Render from what came back, not from what we sent: the BFF trims, normalises and
      // echoes the SAVED state, and disagreeing with it is how a page starts lying.
      const saved = await wasloApi.updateConnection(
        accountId.value,
        connectionId.value,
        patch.value
      );
      adopt(saved);
      savedSections.value = nameSections(carried);
      savedAt.value = new Date();
      useAlert(t('WASLO.SETTINGS.CONNECTIONS.EDITOR.SAVED'));
      historyRef.value?.reload();
    }
  } catch (error) {
    saveFailed.value = true;
    if (!applyServerFields(error)) {
      // No field to pin it to: the sidecar's own refusal, or the connection. Either way the
      // draft is still on screen and the bar carries the sentence.
      formError.value =
        error?.code === WASLO_ERROR_CODES.SERVER && error?.message
          ? error.message
          : '';
    }
    useAlert(t('WASLO.SETTINGS.CONNECTIONS.EDITOR.SAVE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};

// ── the dry run ────────────────────────────────────────────────────────────────────
// Its state is declared beside `adopt`, above: a save is what invalidates it.

/**
 * The stamps the sidecar wrote for a real call, read back so the switch and the sentence
 * under it follow the test without reloading the page. A failed re-read changes nothing:
 * the next load shows them.
 */
const refreshTestStamps = async () => {
  try {
    const fresh = await wasloApi.getConnection(
      accountId.value,
      connectionId.value
    );
    savedRow.value = {
      ...savedRow.value,
      lastTestedAt: fresh.lastTestedAt,
      lastTestOkAt: fresh.lastTestOkAt,
      // P-2.11: a good test keeps its answer as the sample.
      sampleCapturedAt: fresh.sampleCapturedAt ?? null,
    };
  } catch {
    // Deliberately quiet: see above.
  }
};

const onTry = async (values, options = {}) => {
  if (isTrying.value || connectionId.value === null) return;
  isTrying.value = true;
  tryError.value = '';
  tryFields.value = {};
  const askedOf = savedGeneration.value;
  try {
    // P-3.7: the sample customer rides along only when she typed one.
    const sample = options.sample ?? {};
    const answer = await wasloApi.tryConnection(
      accountId.value,
      connectionId.value,
      values,
      {
        send: options.send === true,
        ...(Object.keys(sample).length > 0 ? { sample } : {}),
      }
    );
    // A preview sent nothing; anything else reached her system and was stamped — even
    // when a save landed meanwhile, so the stamps are read back either way.
    if (!answer?.preview) await refreshTestStamps();
    if (askedOf !== savedGeneration.value) return;
    tryResult.value = answer;
  } catch (error) {
    if (askedOf !== savedGeneration.value) return;
    tryResult.value = null;
    // A value she typed here, or a call that could not be made at all. Two different
    // sentences, and the contract makes them distinguishable without parsing prose.
    if (error?.fields) tryFields.value = error.fields;
    else
      tryError.value = t('WASLO.SETTINGS.CONNECTIONS.EDITOR.TRY.UNAVAILABLE');
  } finally {
    isTrying.value = false;
  }
};

// ── the sample (P-2.11) ──────────────────────────────────────────────────────────────
/**
 * The answer from the last good test, kept so Test it can rehearse with it. It can hold her
 * customers' data, so she can forget it; the next good test keeps a new one.
 */
const sampleLine = computed(() => {
  const iso = savedRow.value?.sampleCapturedAt;
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const date = new Intl.DateTimeFormat(toIntlLocale(locale.value), {
    dateStyle: 'medium',
  }).format(at);
  return t('WASLO.SETTINGS.CONNECTIONS.EDITOR.TRY.KEPT_SAMPLE.SAVED', { date });
});
const isForgettingSample = ref(false);
const forgetSample = async () => {
  if (isForgettingSample.value || connectionId.value === null) return;
  isForgettingSample.value = true;
  try {
    await wasloApi.forgetConnectionSample(accountId.value, connectionId.value);
    savedRow.value = { ...savedRow.value, sampleCapturedAt: null };
    useAlert(t('WASLO.SETTINGS.CONNECTIONS.EDITOR.TRY.KEPT_SAMPLE.FORGOTTEN'));
  } catch {
    useAlert(
      t('WASLO.SETTINGS.CONNECTIONS.EDITOR.TRY.KEPT_SAMPLE.FORGET_FAILED')
    );
  } finally {
    isForgettingSample.value = false;
  }
};

// ── unsaved work ───────────────────────────────────────────────────────────────────
/**
 * THE SIGNATURE IS THE WHOLE BUG `PersonalityPage.vue` ONCE SHIPPED, so it is repeated
 * here rather than rediscovered: DECLARE NO PARAMETERS. `vue-router.mjs` calls every
 * component guard as `guard.call(instance, to, from, next)`, so a single declared
 * parameter receives the target ROUTE, not `next` — and with `guard.length === 1` the
 * router resolves the real `next` with `undefined` and the navigation goes through
 * anyway. The guard then fails OPEN, which is the exact thing it exists to prevent.
 * With no parameters the return value is awaited and passed to `next`: `false` aborts,
 * anything else proceeds. `specs/settings.spec.js` pins the arity for that reason.
 */
const pendingLeave = ref(null);
const isConfirmingLeave = computed(() => pendingLeave.value !== null);

/**
 * Whether the docked bar — the only place this page asks anything — is on screen at all.
 * The same four conditions `WasloPageLayout`'s `showFooter` reads, so the guard and the
 * bar can never disagree about whether there is a button to press.
 */
const canAskAboutLeaving = computed(
  () => !isFetching.value && !errorMessage.value
);

onBeforeRouteLeave(() => {
  // A navigation this page itself just made — after a create, after a removal — is not
  // abandoned work. It is the work.
  if (bypassGuard.value) {
    bypassGuard.value = false;
    return true;
  }
  if (!isDirty.value) return true;
  // NOWHERE TO ASK MEANS LET HER GO — `PersonalityPage`'s rule, for the same reason and
  // against the same failure. `WasloPageLayout` hides the docked bar while the page is
  // fetching, in an error state or unprovisioned, and a guard that parks a promise no
  // button can resolve traps somebody on a screen forever. Being trapped is worse than
  // the draft this exists to protect, and it is what the first version of that guard did.
  if (!canAskAboutLeaving.value) return true;
  return new Promise(resolve => {
    pendingLeave.value = resolve;
  });
});

/** `false` aborts and leaves the URL where it was; `true` lets it through. */
const resolveLeave = allowed => {
  const resolve = pendingLeave.value;
  pendingLeave.value = null;
  if (resolve) resolve(allowed);
};

/**
 * THE CLOSING TAB, which no route guard can see. `onBeforeRouteLeave` covers a click
 * inside the dashboard and nothing else. Armed by the same state and removed the moment
 * there is nothing to lose — a page that interrupts a reload it has no reason to
 * interrupt is the fastest way to teach somebody to click through a warning unread.
 * `preventDefault()` AND `returnValue` because browsers disagree about which arms it;
 * the string is ignored everywhere, so there is nothing here to translate.
 */
const warnOnUnload = event => {
  event.preventDefault();
  event.returnValue = '';
};

let unloadWarningArmed = false;

const armUnloadWarning = wanted => {
  if (wanted === unloadWarningArmed) return;
  unloadWarningArmed = wanted;
  if (wanted) window.addEventListener('beforeunload', warnOnUnload);
  else window.removeEventListener('beforeunload', warnOnUnload);
};

watch(isDirty, armUnloadWarning);
onBeforeUnmount(() => armUnloadWarning(false));

// ── removal ────────────────────────────────────────────────────────────────────────
// Two clicks in place rather than a modal, exactly as the task editor does it: the thing
// being confirmed is one sentence long and fits where the button was, which also keeps the
// sentence explaining what removal costs on screen above it.
const remove = async () => {
  if (isRemoving.value || connectionId.value === null) return;
  isRemoving.value = true;
  try {
    await wasloApi.deleteConnection(accountId.value, connectionId.value);
    useAlert(t('WASLO.SETTINGS.CONNECTIONS.EDITOR.REMOVE.REMOVED'));
    if (listUrl.value) {
      // The leave guard must not ask whether to abandon unsaved edits to a connection
      // that no longer exists. Armed only where a navigation really follows, so a failed
      // removal cannot leave the next navigation unguarded.
      bypassGuard.value = true;
      await router.replace(listUrl.value);
    }
  } catch {
    confirmingRemoval.value = false;
    useAlert(t('WASLO.SETTINGS.CONNECTIONS.EDITOR.REMOVE.FAILED'));
  } finally {
    isRemoving.value = false;
  }
};

const removeKey = name => `WASLO.SETTINGS.CONNECTIONS.EDITOR.REMOVE.${name}`;

const reload = () => {
  if (!isNew.value) loadConnection();
};
</script>

<template>
  <!--
    The way back sits in the header row, the same as every other detail page in the section
    (Answers source, Task editor, Broadcast compose and detail). `BackButton`'s DEFAULT label
    comes from a core table, but `backLabel` overrides it, so the words are still ours and
    still bilingual.
  -->
  <WasloPageLayout
    :header-title="headerTitle"
    :back-url="backTo"
    :back-label="backLabel"
    :is-fetching="isFetching"
    :error-message="errorMessage"
    @retry="reload"
  >
    <template #body>
      <div class="flex flex-col gap-6">
        <ConnectionForm
          :model-value="draft"
          :errors="fieldErrors"
          :is-new="isNew"
          :sign-in-saved="signInSaved"
          :saved-name="savedRow?.name || ''"
          :saved-url="savedRow?.url || ''"
          :can-switch-on="canSwitchOn"
          :test-state="testState"
          @update:model-value="onDraftChange"
        >
          <!-- The dry run exists only once there is something saved to call. -->
          <template v-if="!isNew" #try>
            <span class="w-full h-px mt-2 bg-n-weak" />
            <div class="flex flex-col gap-6">
              <SettingsHeader
                :heading="t('WASLO.SETTINGS.CONNECTIONS.EDITOR.TRY.HEADING')"
                :description="
                  t('WASLO.SETTINGS.CONNECTIONS.EDITOR.TRY.DESCRIPTION')
                "
              />
              <ConnectionTestPanel
                :params="savedRow?.params || []"
                :contact-details="savedContactDetails"
                :needs-sign-in="savedNeedsSignIn"
                :is-dirty="isDirty"
                :is-running="isTrying"
                :result="tryResult"
                :error-message="tryError"
                :field-errors="tryFields"
                :purpose="purposeOf(savedRow) ?? 'lookup'"
                @run="onTry"
              />
              <div
                v-if="sampleLine"
                class="flex flex-wrap items-center gap-3"
                data-waslo-connection-sample
              >
                <p class="mb-0 text-sm text-n-slate-11">{{ sampleLine }}</p>
                <Button
                  :label="
                    t(
                      'WASLO.SETTINGS.CONNECTIONS.EDITOR.TRY.KEPT_SAMPLE.FORGET'
                    )
                  "
                  variant="faded"
                  color="slate"
                  size="sm"
                  :is-loading="isForgettingSample"
                  :disabled="isForgettingSample"
                  data-waslo-connection-sample-forget
                  @click="forgetSample"
                />
              </div>
            </div>
          </template>

          <!-- Removal only exists once there is something to remove. -->
          <template v-if="!isNew" #danger>
            <span class="w-full h-px mt-2 bg-n-weak" />
            <div class="flex flex-col gap-6">
              <SettingsHeader
                :heading="t(removeKey('HEADING'))"
                :description="t(removeKey('DESCRIPTION'))"
              />
              <div
                v-if="confirmingRemoval"
                class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-ruby-8 bg-n-solid-1"
                data-waslo-connection-remove
              >
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ t(removeKey('CONFIRM')) }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ t(removeKey('CONFIRM_BODY')) }}
                </p>
                <div class="flex flex-wrap items-center gap-3">
                  <Button
                    :label="t(removeKey('CONFIRM_ACTION'))"
                    variant="solid"
                    color="ruby"
                    size="sm"
                    :is-loading="isRemoving"
                    data-waslo-connection-remove-confirm
                    @click="remove"
                  />
                  <Button
                    :label="t(removeKey('CANCEL'))"
                    variant="faded"
                    color="slate"
                    size="sm"
                    @click="confirmingRemoval = false"
                  />
                </div>
              </div>
              <Button
                v-else
                :label="t(removeKey('ACTION'))"
                icon="i-lucide-trash-2"
                variant="faded"
                color="ruby"
                size="sm"
                class="self-start"
                data-waslo-connection-remove-start
                @click="confirmingRemoval = true"
              />
            </div>
          </template>
        </ConnectionForm>
        <!-- P-6.11: this connection's saves. A restore keeps the sign-in stored now. -->
        <RestoreHistory
          v-if="!isNew"
          ref="historyRef"
          :account-id="accountId"
          kind="connection"
          :item-id="connectionId"
          @restored="loadConnection"
        />
      </div>
    </template>

    <!-- Docked, outside the scrollport, on screen at every scroll position. -->
    <template #footer>
      <!--
        THE LEAVE QUESTION, ASKED WHERE THE SAVE BUTTON WAS. Not a dialog: every
        teleported overlay in this codebase reaches the Vuex store through
        `TeleportWithDirection` and throws inside a render effect AFTER a unit test has
        resolved, so it can never fail an assertion — only the exit code, which is how the
        image build died on both environments. Asking in the docked bar also puts the
        question exactly where the thing she has not pressed lives.
      -->
      <div
        v-if="isConfirmingLeave"
        class="flex flex-wrap items-center gap-3"
        data-waslo-connection-leave
      >
        <div class="flex flex-col min-w-0 grow">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('WASLO.SETTINGS.CONNECTIONS.EDITOR.LEAVE.TITLE') }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('WASLO.SETTINGS.CONNECTIONS.EDITOR.LEAVE.BODY') }}
          </p>
        </div>
        <Button
          :label="t('WASLO.SETTINGS.CONNECTIONS.EDITOR.LEAVE.STAY')"
          size="sm"
          data-waslo-connection-leave-stay
          @click="resolveLeave(false)"
        />
        <Button
          :label="t('WASLO.SETTINGS.CONNECTIONS.EDITOR.LEAVE.CONFIRM')"
          variant="faded"
          color="ruby"
          size="sm"
          data-waslo-connection-leave-confirm
          @click="resolveLeave(true)"
        />
      </div>
      <WasloSaveBar
        v-else
        :state="saveState"
        :status="saveStatus"
        :detail="saveDetail"
        :save-label="saveLabel"
        :disabled="isSaving"
        @save="save"
      />
    </template>
  </WasloPageLayout>
</template>
